import { useState } from 'react';
import { Gift, X, AlertCircle, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import * as analytics from '../services/analytics';

/**
 * Shared Claim Request Modal — used on GiveawaysPage, ProfilePage, and CommunityGiveawaysTab.
 * Props:
 *   item     - the giveaway item object { id, title }
 *   onClose  - called when modal is dismissed
 *   onSuccess(itemId) - called after a successful claim request submission
 */
export default function ClaimRequestModal({ item, onClose, onSuccess }) {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/items/${item.id}/claim-request`, { claimer_note: note });
      toast.success(r.data.message);
      analytics.track('giveaway_claimed', { item_id: item.id, category: item.category });
      onSuccess(item.id);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send claim request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-stone-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
              <Gift size={20} className="text-pink-500"/>
            </div>
            <div>
              <h3 className="font-poppins font-semibold text-gray-900">Request to Claim</h3>
              <p className="text-xs text-gray-500 font-nunito line-clamp-1">{item.title}</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="close-claim-modal">
            <X size={18} className="text-gray-400 hover:text-gray-600"/>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-sm text-amber-800 font-nunito flex items-start gap-2">
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5 text-amber-500"/>
            <span>The owner must <strong>approve</strong> your request before pickup. They'll share pickup details once approved.</span>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-nunito">
              Message to owner <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Hi! I'd love this item. I can pick up this weekend..."
              rows={3}
              data-testid="claim-note-input"
              className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-400/20 outline-none font-nunito text-sm bg-white transition-all resize-none"
            />
          </div>
        </div>

        <div className="flex gap-3 p-6 border-t border-stone-100">
          <button
            onClick={onClose}
            className="flex-1 border-2 border-stone-200 text-gray-700 py-2.5 rounded-xl font-nunito font-semibold hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="submit-claim-request"
            className="flex-1 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white py-2.5 rounded-xl font-nunito font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 size={16} className="animate-spin"/> : <Send size={15}/>}
            {submitting ? 'Sending…' : 'Send Request'}
          </button>
        </div>
      </div>
    </div>
  );
}
